import { Alert, Button, Checkbox, Divider, Form, Modal, Radio, Space, Tooltip, message } from 'antd';
import { CalendarOutlined, GlobalOutlined } from '@ant-design/icons';
import Title from 'antd/lib/typography/Title';
import { EventService } from '../../Services/EventService';
import { useLocation, useParams } from "react-router-dom"
import { useEffect, useState } from 'react';
import Paragraph from 'antd/lib/typography/Paragraph';
import { formatDateRangeWithDateString, formatDateString, getTimeRangeString } from '../../Helpers/DateHelpers';
import { useSaveReply } from '../../Hooks/useSaveReply';
import { useNavigate } from "react-router-dom";
import { getTimeZones } from '@vvo/tzdb';
import { EventState } from './EventState';
import './Event.css'
import Page from '../../Components/Page/Page';
import BookyBox from '../../Components/Layout/BookyBox';
import NotFound from '../../Components/Page/NotFoundPage';
import EventClosedPage from './EventClosedPage';
import { ReplyService } from '../../Services/ReplyService';
import { PublicSiteService, householdMember } from '../../Services/PublicSiteService';
import { BookyInput, InputType } from '../../Components/Forms/BookyInput';
import { ReplyTimeForm } from '../../Components/Forms/ReplyTimeForm';
import { TextService } from '../../Services/TextService';
import { EventType } from '../../Types/EventType';
import { TimeChoice } from '../../Types/TimeChoice';
import { isOnlyComing } from '../../Types/AnswerOptions';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import { HouseholdAnswers, clearFullHouseholdAnswers, fillNewlyCheckedAnswers, getHouseholdAnswers, setHouseholdAnswers } from './HouseholdAnswers';

enum MemberLogin {
    NotNeeded,
    Checking,
    LoggedOut,
    LinkSent,
    LoggedIn
}

// A time before today has passed and can't be answered any more, the same limit the server uses.
const withoutPassedTimes = (event: any) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return { ...event, times: (event.times ?? []).filter((time: any) => new Date(time.start) >= today) };
}

const Event = () => {
    const [state, setState] = useState([] as any);
    const [event, setEvent] = useState([] as any);
    const [reply, setReply] = useState([] as any);
    const [eventState, setEventState] = useState(EventState.Open as EventState);
    let params = useParams();
    const location = useLocation();
    const { SaveReply, SaveMemberReply, SaveHouseholdReply } = useSaveReply();
    // The people a primary member can sign up, from logging in or from the member's personal invite.
    // Empty when the visitor only answers for themselves.
    const [household, setHousehold] = useState([] as householdMember[]);
    // For an event for members only: whether the visitor has to log in as a member before answering.
    const [memberLogin, setMemberLogin] = useState(MemberLogin.NotNeeded as MemberLogin);
    const [loginForm] = Form.useForm();
    const navigation = useNavigate();
    const [form] = Form.useForm();
    const GetText = TextService.GetText;

    useEffect(() => {
        if (params.id != null) {
            EventService.GetEvent(params.id).then(loaded => {
                const result = withoutPassedTimes(loaded);

                // Set first, so a closed event still shows its name.
                setEvent(result);

                if (result.deadline != null && new Date() > new Date(result.deadline)) {
                    setEventState(EventState.DeadlinePassed);
                    return;
                }

                // Every time has passed, so there is nothing left to answer.
                if (result.state === EventState.Closed || (loaded.times?.length > 0 && result.times.length === 0)) {
                    setEventState(EventState.Closed);
                    return;
                }

                // Fills in the answers of a reply the visitor already has.
                const setAnswers = (replyTimes: any[] | undefined, hasReply: boolean) => {
                    const isSignUp = result.eventType !== EventType.Poll;

                    replyTimes?.forEach((replyTime: any) => {
                        // "Maybe" is no longer an answer for a sign-up, so an old one has to be answered again.
                        if (isSignUp && replyTime.customerAnswerType === 1) {
                            return;
                        }

                        form.setFieldValue(replyTime.timeId, replyTime.customerAnswerType.toString());
                    });

                    if (isSignUp && result.timeChoice === TimeChoice.One && result.times?.length > 1) {
                        const chosen = replyTimes?.find((replyTime: any) => replyTime.customerAnswerType === 0 || replyTime.customerAnswerType === 3);
                        // With only "Kommer" there is no "Kommer inte" to choose, so a cancelled reply starts unanswered.
                        form.setFieldValue("singleChoice", chosen ? chosen.timeId : (hasReply && !isOnlyComing(result) ? "none" : undefined));
                    }
                };

                // A primary member signs up the household, each person with own answers. Those already
                // signed up are checked, or else the member.
                const setHouseholdValues = (people: householdMember[]) => {
                    setHousehold(people);
                    const signedUp = people.filter(p => p.replyTime.some(r => r.customerAnswerType !== 2));
                    form.setFieldValue("memberIds", (signedUp.length > 0 ? signedUp : people.filter(p => p.isSelf)).map(p => p.memberId));
                    setHouseholdAnswers(form, result, people);
                };

                // A personal invite or the link to an existing reply already says who the visitor is.
                // Otherwise the visitor answers as a member when the event requires it, or when the
                // visitor comes from the member site and already is logged in.
                const comesFromMemberSite = (location.state as any)?.asMember === true;
                if ((result.membersOnly === true || comesFromMemberSite) && params.inviteId == null && params.authCode == null) {
                    setMemberLogin(MemberLogin.Checking);
                    PublicSiteService.GetEventReply(result.uniqueId).then(memberReply => {
                        setReply(memberReply);

                        form.setFieldsValue({
                            name: memberReply.name,
                            email: memberReply.email,
                            answer: memberReply.message,
                        });

                        if (memberReply.household?.length > 0) {
                            setHouseholdValues(memberReply.household);
                        } else {
                            setAnswers(memberReply.replyTime, memberReply.replyId > 0);
                        }

                        setMemberLogin(MemberLogin.LoggedIn);
                    }).catch(() => {
                        // An event that is open for all can be answered without logging in.
                        setMemberLogin(result.membersOnly === true ? MemberLogin.LoggedOut : MemberLogin.NotNeeded);
                    });

                    return;
                }

                if (params.id != null && (params.inviteId != null || params.authCode != null)) {
                    ReplyService.GetReplyByInviteIdOrAuthCode(params.id, params.inviteId != null ? params.inviteId : "", params.authCode != null ? params.authCode : "").then(existingReply => {
                        setReply(existingReply);

                        form.setFieldsValue({
                            name: existingReply.name,
                            email: existingReply.email,
                            answer: existingReply.message,
                        });

                        if (existingReply.household?.length > 0) {
                            setHouseholdValues(existingReply.household);
                        } else {
                            setAnswers(existingReply.replyTime ?? undefined, (existingReply.replyId ?? 0) > 0);
                        }
                    });
                }

            }).catch((error) => {
                setEventState(EventState.NotFound);
            });
        }
    }, [params, form])

    const onMemberLoginRequest = async (formValues: any) => {
        try {
            await PublicSiteService.CreatePasswordCode({
                companyUrl: event.companySiteUrl,
                email: formValues.email,
                eventUniqueId: event.uniqueId
            });
        } catch {
            message.warning(GetText("PublicSiteEditMember_Error"));
            return;
        }

        // The same message whether or not the e-mail belongs to a member, so it can't be used to look members up.
        setMemberLogin(MemberLogin.LinkSent);
    }

    // cancel: "Avanmäl mig" on a sign-up with only "Kommer". Every time becomes a "no", which frees the places.
    const FormCreateClick = async (submittedValues: any, cancel: boolean = false) => {
        let result: any;
        const declineAll = () => (event.times ?? []).map((time: any) => ({ timeId: time.id, customerAnswer: 2 }));
        const formValues = cancel
            ? { ...submittedValues, ...Object.fromEntries((event.times ?? []).map((time: any) => [time.id, "2"])) }
            : getTimeValues(submittedValues);
        const people = household.length === 0 ? undefined
            : cancel ? household.filter(person => person.replyId > 0).map(person => ({ memberId: person.memberId, timeAnswers: declineAll() }))
                : getHouseholdAnswers(form, event, household, submittedValues.memberIds ?? []);

        // With only "Kommer" an unanswered time is a "no", so the guest (and each chosen person) has to come to some time.
        const comes = (answers: { customerAnswer: number }[]) => answers.some(answer => answer.customerAnswer === 0 || answer.customerAnswer === 3);
        if (onlyComing && !cancel) {
            const everyoneComes = people != null
                ? people.every(person => comes(person.timeAnswers))
                : comes((event.times ?? []).map((time: any) => ({ customerAnswer: Number(formValues[time.id]) })));

            if (!everyoneComes) {
                message.warning(GetText("Event_ChooseAtLeastOneTime"));
                return;
            }
        }

        try {
            if (memberLogin === MemberLogin.LoggedIn) {
                result = await SaveMemberReply(event.uniqueId, formValues, event.times, people);
            } else if (people != null && params.inviteId != null) {
                result = await SaveHouseholdReply(event.id, params.inviteId, submittedValues.answer, people);
            } else {
                result = await SaveReply(event.id, formValues, event.times, params.inviteId, reply != null ? reply.replyId : null, params.authCode);
            }
        } catch (error: any) {
            if ((error?.response?.status ?? error?.status) !== 409) {
                message.error(GetText("Common_TempError"));
                return;
            }

            // Someone else took the last place. Reload so the full times show, and drop the answers that can't be saved.
            message.error(GetText("Event_FullyBookedError"));
            const updatedEvent = withoutPassedTimes(await EventService.GetEvent(event.uniqueId));
            updatedEvent.times?.forEach((time: any) => {
                const answer = form.getFieldValue(time.id);
                if ((answer === "0" && isFullyBooked(time)) || (answer === "3" && !isWaitlistOpen(time))) {
                    form.setFieldValue(time.id, undefined);
                }
            });
            // The chosen time may be the one that got full, so the guest has to choose again.
            form.setFieldValue("singleChoice", undefined);
            clearFullHouseholdAnswers(form, updatedEvent, household);
            setEvent(updatedEvent);
            return;
        }

        // The server decides who got a place and who is on the waitlist, so the result is shown from its answer.
        const getTimeTexts = (timeIds?: number[]) => (event.times ?? [])
            .filter((time: any) => timeIds?.includes(time.id))
            .map((time: any) => formatDateRangeWithDateString(time.start, time.end, time.isFullDay, getCurrentLanguage() === "se"));

        // The times someone got a place in can be added to a calendar. A poll only suggests times.
        const placeTimeIds: number[] = Array.from(new Set<number>([
            ...(result?.acceptedTimeIds ?? []),
            ...(result?.people ?? []).flatMap((person: any) => person.acceptedTimeIds ?? [])
        ]));
        const calendarUrl = isSignUp && placeTimeIds.length > 0
            ? process.env.REACT_APP_API_URL + "/Reply/calendar/" + encodeURIComponent(event.uniqueId) + "?" + placeTimeIds.map(id => "timeIds=" + id).join("&")
            : undefined;

        navigation("/" + getCurrentLanguage() + "/event/completed", {
            state: {
                cancelled: cancel,
                calendarUrl: calendarUrl,
                name: event.name,
                acceptedTimes: getTimeTexts(result?.acceptedTimeIds),
                waitlistedTimes: getTimeTexts(result?.waitlistedTimeIds),
                hasSeveralTimes: event.times?.length > 1,
                // When a household was signed up, what each person got.
                people: (result?.people ?? []).map((person: any) => ({
                    name: person.name,
                    acceptedTimes: getTimeTexts(person.acceptedTimeIds),
                    waitlistedTimes: getTimeTexts(person.waitlistedTimeIds)
                }))
            }
        });
    }

    let timeZoneInfo = ""
    getTimeZones().forEach(element => {
        if (element.name === event.timeZoneId) {
            timeZoneInfo = element.currentTimeFormat;
        }
    });

    // The time zone is seldom needed, so it is a small globe beside the times that tells it on hover or tap.
    const timeZoneHint = timeZoneInfo ?
        <Tooltip title={GetText("Event_TimezoneTooltip") + " " + timeZoneInfo} trigger={["hover", "click"]}>
            <GlobalOutlined data-testid="time-zone-hint" aria-label={GetText("Event_TimezoneTooltip") + " " + timeZoneInfo} style={{ color: "#999", marginLeft: 6, cursor: "help" }} />
        </Tooltip> : null;

    if (eventState === EventState.NotFound) {

        return <div className="NoChromePage">
            <NotFound message={GetText("Event_EventNotFound")} />
        </div>
    }

    if (eventState === EventState.DeadlinePassed || eventState === EventState.Closed) {

        return <div className="NoChromePage">
            <EventClosedPage event={event} deadlinePassed={eventState === EventState.DeadlinePassed} />
        </div>
    }

    const getAnswerByTimeId = (timeId: string) => {
        return form.getFieldValue(timeId);
    };

    const onChangeAnswerComplete = (timeId: string, answer: string) => {
        form.setFieldValue(timeId, answer);
        setState(!state);
    }

    // A member without e-mail that an admin has added. The reply can be changed without adding an e-mail.
    const isReplyWithoutEmail = reply?.replyId > 0 && !reply?.email;

    // A primary member answering for the household answers for each person in HouseholdAnswers.
    const isHousehold = household.length > 0;

    const onHouseholdChecked = (memberIds: any[]) => {
        fillNewlyCheckedAnswers(form, event, household, memberIds as number[]);
    };

    const hasOwnAnswer = (time: any, answerType: number) => {
        return reply?.replyTime?.some((replyTime: any) => replyTime.timeId === time.id && replyTime.customerAnswerType === answerType) === true;
    }

    // A time the visitor already has a place in is never shown as full, so the answer can still be changed.
    const isFullyBooked = (time: any) => {
        return time.isFullyBooked === true && !hasOwnAnswer(time, 0);
    }

    // A visitor who already is on the waitlist stays there, also when the waitlist has become full.
    const isWaitlistOpen = (time: any) => {
        return isFullyBooked(time) && (time.isWaitlistOpen === true || hasOwnAnswer(time, 3));
    }

    const isSignUp = event.eventType !== EventType.Poll;

    // What a person in the household already has: a place, the waitlist, or another answer.
    const getHouseholdStatus = (person: householdMember) => {
        if (person.replyId === 0) {
            return "";
        }

        if (isSignUp && person.replyTime.some(r => r.customerAnswerType === 0)) {
            return GetText("Event_HouseholdSignedUp");
        }

        if (person.replyTime.some(r => r.customerAnswerType === 3)) {
            return GetText("Event_HouseholdOnWaitlist");
        }

        return GetText("Event_HouseholdAnswered");
    };

    // The guest picks one of the times instead of answering each of them.
    const isSingleChoice = isSignUp && event.timeChoice === TimeChoice.One && event.times?.length > 1;

    // A sign-up where "Kommer" is the only answer. With one time there is nothing to choose, so the guest
    // answers with the "Anmäl mig" button alone.
    const onlyComing = isOnlyComing(event);
    const isOneTime = event.times?.length === 1;
    const isOneTimeSignUp = onlyComing && isOneTime;

    // With one time to pick, the chosen time is a sign-up (or the waitlist when it is full) and the others a "no".
    // With only "Kommer", a time left unanswered is a "no", which also frees a place the guest had in it.
    const getTimeValues = (formValues: any) => {
        if (!isSingleChoice) {
            if (!onlyComing) {
                return formValues;
            }

            const values = { ...formValues };
            event.times.forEach((time: any) => {
                // With one time, "Anmäl mig" is the answer: a place, or the waitlist when the time is full.
                values[time.id] = isOneTimeSignUp ? (isFullyBooked(time) ? "3" : "0") : values[time.id] ?? "2";
            });

            return values;
        }

        const values = { ...formValues };
        event.times.forEach((time: any) => {
            values[time.id] = formValues.singleChoice === time.id ? (isFullyBooked(time) ? "3" : "0") : "2";
        });

        return values;
    };

    // With "yes" as the only answer option there is nothing left to answer once every time is full and has no waitlist.
    const hasNothingToAnswer = (!isSignUp || onlyComing) && event.answerOptions === 2 && event.times?.length > 0
        && event.times.every((time: any) => isFullyBooked(time) && !isWaitlistOpen(time));

    // Every time is full for the visitor. A time the visitor already has a place in is never full for them.
    // A poll can't be fully booked.
    // The visitor's own reply comes first on the page, so the visitor sees at once where they stand.
    const ownAnswers: any[] = !isHousehold && reply?.replyId > 0 ? reply?.replyTime ?? [] : [];
    const hasOwnPlace = isSignUp && ownAnswers.some((replyTime: any) => replyTime.customerAnswerType === 0);
    const isOwnWaitlist = isSignUp && !hasOwnPlace && ownAnswers.some((replyTime: any) => replyTime.customerAnswerType === 3);
    const hasDeclined = isSignUp && !onlyComing && ownAnswers.length > 0 && ownAnswers.every((replyTime: any) => replyTime.customerAnswerType === 2);
    const isAllFullyBooked = isSignUp && !isHousehold && !hasOwnPlace && !isOwnWaitlist
        && event.times?.length > 0 && event.times.every((time: any) => isFullyBooked(time));
    const isAnyWaitlistOpen = event.times?.some((time: any) => isWaitlistOpen(time)) === true;

    // The guest (or someone in the household) has a place or is on the waitlist, and can cancel it.
    const holdsPlace = (replyTimes?: any[]) => replyTimes?.some((replyTime: any) => replyTime.customerAnswerType === 0 || replyTime.customerAnswerType === 3) === true;
    const canCancel = onlyComing && (isHousehold ? household.some(person => holdsPlace(person.replyTime)) : holdsPlace(reply?.replyTime));

    const onCancelSignUp = () => {
        Modal.confirm({
            title: GetText("Event_CancelSignUpConfirm"),
            okText: GetText(isHousehold ? "Event_CancelHouseholdSignUp" : "Event_CancelSignUp"),
            okButtonProps: { danger: true, ...({ "data-testid": "cancel-signup-confirm" } as any) },
            cancelText: GetText("Common_Cancel"),
            onOk: () => FormCreateClick(form.getFieldsValue(true), true),
        });
    }

    const eventHeader = <>
                {


                    event.bannerImagePath ? <div style={{ marginBottom: "20px" }}>
                        <div className='hero-image' style={{
                            backgroundImage: `url(${(process.env.REACT_APP_API_URL + "/Image/downloadImage?bloburl=" + event.bannerImagePath)}`
                        }} >
                        </div>
                        <div><Title style={{ margin: 0, color: event.bannerImageColorCode ?? undefined }}>{event.name}</Title></div>
                    </div> : <Space style={{ width: '100%', justifyContent: 'left', margin: 0 }}>
                        {/* The chosen title color applies with or without an image. */}
                        <Title style={{ margin: 0, color: event.bannerImageColorCode ?? undefined }}>{event.name}</Title>
                    </Space>}


                {/* Empty fields are left out, so they don't leave gaps in the header. */}
                {event.description ? <Space style={{ width: '100%', justifyContent: 'left', margin: 0 }}>
                    {/* pre-line keeps the line breaks and blank lines the admin typed in the description. */}
                    <Paragraph style={{ whiteSpace: 'pre-line' }}>{event.description}</Paragraph>
                </Space> : null}
                {/* With one time there is nothing to choose between, so the time is told here like the place,
                    and the form only asks for the answer. Several times are listed in the form. */}
                {isOneTime ? <Space style={{ width: '100%', justifyContent: 'left', margin: 0 }}>
                    <Paragraph data-testid="event-time">
                        <CalendarOutlined style={{ marginRight: 6 }} />
                        <b>{formatDateRangeWithDateString(event.times[0].start, event.times[0].end, event.times[0].isFullDay, getCurrentLanguage() === "se")}</b>
                        {timeZoneHint}
                    </Paragraph>
                </Space> : null}
                {event.location ? <Space style={{ width: '100%', justifyContent: 'left', margin: 0 }}>
                    <Paragraph>{event.location}</Paragraph>
                </Space> : null}
                {event.deadline ? <Space style={{ width: '100%', justifyContent: 'left', margin: 0 }}>
                    <Paragraph>{GetText("Event_EventLastDayReply")} <b>{formatDateString(event.deadline)}</b></Paragraph>
                </Space> : null}
                <Divider />
    </>;

    if (memberLogin === MemberLogin.Checking) {
        return <Page className="NoChromePage">
            <BookyBox>
                {eventHeader}
            </BookyBox>
        </Page>
    }

    if (memberLogin === MemberLogin.LoggedOut || memberLogin === MemberLogin.LinkSent) {
        return <Page className="NoChromePage">
            <BookyBox>
                {eventHeader}
                <Title level={3} style={{ textAlign: "left", marginTop: 0 }}>{GetText("Event_MembersOnlyTitle")}</Title>

                {memberLogin === MemberLogin.LinkSent ?
                    <Paragraph style={{ textAlign: "left" }}>{GetText("Event_MembersOnlyLinkSent")}</Paragraph>
                    :
                    <>
                        <Paragraph style={{ textAlign: "left" }}>{GetText("Event_MembersOnlyDescription")}</Paragraph>
                        <Form
                            data-testid="member-login-form"
                            name="memberLogin"
                            form={loginForm}
                            colon={false}
                            labelCol={{ span: 7 }}
                            wrapperCol={{ span: 15 }}
                            layout="horizontal"
                            onFinish={onMemberLoginRequest}
                            autoComplete="off"
                        >
                            <BookyInput label={GetText("PublicSiteEditMember_Email")} placeholder={GetText("PublicSiteEditMember_PlaceHolder_Email")} validateEmailIfFilledIn={true} name="email" required={true} requiredMessage={GetText("PublicSiteEditMember_PlaceHolder_Email")} />

                            <Form.Item label=" ">
                                <Button data-testid="member-login-button" style={{ float: 'left', backgroundColor: event.primaryColorCode ? event.primaryColorCode : "var(--color-primary)", color: event.secondaryColorCode ? event.secondaryColorCode : "white" }} size="middle" type="primary" htmlType="submit">
                                    {GetText("Event_MembersOnlyLogin")}
                                </Button>
                            </Form.Item>
                        </Form>
                        <Paragraph style={{ textAlign: "left", fontSize: "80%" }}>{GetText("Event_MembersOnlyNoEmail")}</Paragraph>
                    </>
                }
            </BookyBox>
        </Page>
    }

    return <Page className="NoChromePage">
        <BookyBox>
            <Form
                data-testid="create-form"
                name="basic"
                form={form}
                colon={false}
                labelCol={{
                    span: 7,
                }}
                onFinish={FormCreateClick}
                wrapperCol={{
                    span: 15,
                }}
                layout="horizontal"
                initialValues={{
                    remember: true,
                }}
                autoComplete="off"
            >
                {eventHeader}

                {/* Told at the top, so nobody fills in the form before seeing that every place is taken. */}
                {isAllFullyBooked ?
                    <Alert data-testid="event-fully-booked" type={isAnyWaitlistOpen ? "warning" : "error"} showIcon style={{ textAlign: "left", marginBottom: 16 }}
                        message={GetText("Event_FullyBookedTitle")}
                        description={GetText(isAnyWaitlistOpen ? "Event_FullyBookedWaitlistInfo" : "Event_FullyBookedInfo")} /> : null}

                {memberLogin === MemberLogin.LoggedIn ? <Paragraph style={{ textAlign: "left" }}><i>{GetText("Event_LoggedInAs")} {reply?.name}</i></Paragraph> : null}

                {hasOwnPlace ?
                    <Alert data-testid="signed-up-status" type="success" showIcon style={{ textAlign: "left", marginBottom: 16 }}
                        message={GetText("Event_YouAreSignedUp")} /> : null}
                {isOwnWaitlist ?
                    <Alert data-testid="waitlist-status" type="info" showIcon style={{ textAlign: "left", marginBottom: 16 }}
                        message={GetText("Event_OnWaitlist")} description={GetText("Event_OnWaitlistInfo")} /> : null}
                {hasDeclined ?
                    <Alert data-testid="declined-status" type="info" showIcon style={{ textAlign: "left", marginBottom: 16 }}
                        message={GetText("Event_YouDeclined")} /> : null}

                <BookyInput label={GetText("Event_Name")} name="name" required={true} requiredMessage={GetText("Event_Name_Placeholder")} disabled={memberLogin === MemberLogin.LoggedIn || isHousehold} />
                <BookyInput label={GetText("Event_Email")} name="email" required={!isReplyWithoutEmail && !isHousehold} type={InputType.email} requiredMessage={isReplyWithoutEmail ? undefined : GetText("Event_Email")} disabled={memberLogin === MemberLogin.LoggedIn || isHousehold} />

                {isHousehold ?
                    <Form.Item
                        label={GetText("Event_HouseholdTitle")}
                        name="memberIds"
                        extra={<div style={{ textAlign: "left" }}>{GetText("Event_HouseholdInfo")}</div>}
                        rules={[{ required: true, type: "array", min: 1, message: GetText("Event_HouseholdRequired") }]}
                    >
                        <Checkbox.Group data-testid="household-checkboxes" onChange={onHouseholdChecked}>
                            <Space direction="vertical" style={{ textAlign: "left" }}>
                                {household.map(person =>
                                    <Checkbox key={person.memberId} value={person.memberId}>
                                        {person.name} {person.isSelf ? GetText("Event_HouseholdYou") : ""}
                                        {getHouseholdStatus(person) ? <i> &ndash; {getHouseholdStatus(person)}</i> : null}
                                    </Checkbox>
                                )}
                            </Space>
                        </Checkbox.Group>
                    </Form.Item>
                    : null}
                <BookyInput label={GetText("Event_MesssageToOrganizer")} name="answer" required={true} message={GetText("Event_MesssageToOrganizer")} />

                {isHousehold ?
                    <HouseholdAnswers form={form} event={event} household={household} timeZoneHint={timeZoneHint} />
                    : isSingleChoice ?
                    <Form.Item
                        label={<>{GetText("Event_ChooseTime")}{timeZoneHint}</>}
                        name="singleChoice"
                        rules={[{ required: true, message: GetText("Event_PlaseAnswerAvailabilityValidationMessage") }]}
                    >
                        <Radio.Group onChange={() => setState(!state)}>
                            <Space direction="vertical" style={{ textAlign: "left" }}>
                                {event.times.map((time: any) =>
                                    <Radio key={time.id} value={time.id} disabled={isFullyBooked(time) && !isWaitlistOpen(time)}>
                                        {formatDateRangeWithDateString(time.start, time.end, time.isFullDay, getCurrentLanguage() === "se")}
                                        {isFullyBooked(time) ? <i> &ndash; {GetText(!isWaitlistOpen(time) ? "Event_TimeFullyBooked" : hasOwnAnswer(time, 3) ? "Event_OnWaitlist" : "Event_FullJoinWaitlist")}</i> : ""}
                                    </Radio>
                                )}
                                {/* With only "Kommer", the guest cancels with "Avanmäl mig" instead. */}
                                {!onlyComing ? <Radio value="none">{GetText("Event_NotComing")}</Radio> : null}
                            </Space>
                        </Radio.Group>
                    </Form.Item>
                    : event.times?.map((time: any, i: any) =>

                    <Form.Item
                        // One time is told in the header, so the row is the answer (or nothing to choose with only "Kommer").
                        label={isOneTime ? (isOneTimeSignUp ? " " : GetText("Event_YourAnswer"))
                            : <>{formatDateRangeWithDateString(time.start, time.end, time.isFullDay, getCurrentLanguage() == "se" ? true : false)}{timeZoneHint}</>}
                        name={time.id}
                        key={time.id}
                        rules={[
                            {
                                // With only "Kommer" a time can be left unanswered.
                                required: !isFullyBooked(time) && !onlyComing,
                                message: GetText("Event_PlaseAnswerAvailabilityValidationMessage"),
                            },
                        ]}
                    >
                        <ReplyTimeForm event={event} time={time} onChangeAnswerComplete={onChangeAnswerComplete} answer={getAnswerByTimeId(time.id)} isFullyBooked={isFullyBooked(time)} isWaitlistOpen={isWaitlistOpen(time)} isOnWaitlist={hasOwnAnswer(time, 3)} hideAnswers={isOneTimeSignUp} hideOwnStatus={isOneTime} />
                    </Form.Item>
                )}

                <Form.Item
                    label=" "
                    name="submit"
                    rules={[
                        {
                            required: false,
                            message: 'submitbutton',
                        },
                    ]}
                >
                    <Space style={{ float: 'left' }} wrap>
                        <Button data-testid="submit-button" style={{ backgroundColor: event.primaryColorCode ? event.primaryColorCode : "var(--color-primary)", color: event.secondaryColorCode ? event.secondaryColorCode : "white" }} size="middle" type="primary" htmlType="submit" disabled={hasNothingToAnswer}>
                            {reply?.replyId > 0 || household.some(p => p.replyId > 0) ? GetText("Event_UpdateAnswer")
                                // A household signs up the checked people, and each gets a place or the waitlist, so it is just "Anmäl".
                                : isOneTimeSignUp ? GetText(isHousehold ? "Event_SignUpHouseholdButton" : isFullyBooked(event.times[0]) ? "Event_JoinWaitlist" : "Event_SignUpButton")
                                    : GetText("Event_SubmitAnswer")}
                        </Button>
                        {canCancel ?
                            <Button data-testid="cancel-signup-button" size="middle" danger onClick={onCancelSignUp}>
                                {GetText(isHousehold ? "Event_CancelHouseholdSignUp" : "Event_CancelSignUp")}
                            </Button> : null}
                    </Space>
                </Form.Item>
            </Form>
        </BookyBox>
    </Page>
}

export default Event;